import { UserEndpoints } from '@xemplo/gp-constants';
import { CreateUserType, PersonaRole, UserPostApiActions } from '@xemplo/gp-types';
import { useRoleAuthorisation } from '@xemplo/roles-query';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

const defaultParams = { page: 1, per_page: 500 };

export function useUsersUrls() {
  const { applicationApiUrl } = useAppSettings();
  const { data } = useRoleAuthorisation();

  const providersUrl = apiUrlBuilder(
    UserEndpoints.listServiceProviders,
    applicationApiUrl,
    defaultParams
  );

  const companyUrl = apiUrlBuilder(
    UserEndpoints.listCompanies,
    applicationApiUrl,
    defaultParams
  );

  const rolesUrl = (userType: CreateUserType) => {
    const rolePrefix = userType === CreateUserType.CompanyUser ? 'CU' : 'PS';
    return apiUrlBuilder(UserEndpoints.listRoles, applicationApiUrl, {
      ...defaultParams,
      q: { rolePrefix },
    });
  };

  const createPSPUserUrl = apiUrlBuilder(UserEndpoints.postPspUser, applicationApiUrl);

  /**
   * When creating a new company user, we also need to check the role
   * from the logged in user. Since the permission is based on the route,
   * depending on the role we have a different endpoint! Yay! =/
   */
  const createCompanyUserUrl = () => {
    const role = data?.result.roleName;
    const endpoint =
      role === PersonaRole.CompanyAdmin
        ? UserEndpoints.postCreateCompanyUserForCA
        : UserEndpoints.postCreateCompanyUser;

    return apiUrlBuilder(endpoint, applicationApiUrl);
  };

  const getCreateUserUrl = (userType: CreateUserType) => {
    return userType === CreateUserType.CompanyUser
      ? createCompanyUserUrl()
      : createPSPUserUrl;
  };

  const postUserActionUrl = (
    activeUserRole: PersonaRole,
    userType: CreateUserType,
    userId: string,
    action: UserPostApiActions
  ) => {
    if (activeUserRole === PersonaRole.CompanyAdmin) {
      return apiUrlBuilder(
        UserEndpoints.postClientUserAction(userId, action),
        applicationApiUrl
      );
    }
    if (userType === CreateUserType.CompanyUser) {
      return apiUrlBuilder(
        UserEndpoints.postCompanyUserAction(userId, action),
        applicationApiUrl
      );
    }

    return apiUrlBuilder(
      UserEndpoints.postPspUserAction(userId, action),
      applicationApiUrl
    );
  };

  return {
    applicationApiUrl,
    providersUrl,
    companyUrl,
    rolesUrl,
    getCreateUserUrl,
    postUserActionUrl,
  };
}
